import { defineStore } from 'pinia'
import { store } from '../index'
import { dayjs } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useAppStore } from '@/store/modules/app'
import {
  logoutRequest,
  getWaterMark,
  getImportRecord,
  getImportResult,
  uspLogoutRequest
} from '@/api'
import { useTagsViewStore } from './tagsView'
import { usePermissionStoreWithOut } from './permission'
import router from '@/router'
import { resetRoot } from '@/utils/routerHelper'
import { transformWaterMarkConfig } from '@/utils'
import ImportProgress from '@/components/ImportExcel/importProgress.vue'

type UserLoginType = {
  username: string
  password: string
}

type UserType = {
  accountCode: string
  // initPwdFlag: boolean
  username: string
  role?: string
  roleId?: string
  permissions?: string | string[]
  expireTime?: string
  telNumber?: string
}

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  waterMark: object
  accountRrror: object
  importRecordRef: HTMLElement | null
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined,
      accountRrror: {
        error: false,
        errorTips: ''
      },
      waterMark: {
        isClose: false, // 是否是关闭状态
        content: '',
        invisibleContent: '未登陆',
        invisibleFontSize: 14,
        font: {
          fontSize: 14,
          color: `rgba(0, 0, 0, 0.2)`
        },
        zIndex: 100,
        rotate: -22,
        gap: [100, 100] as [number, number],
        offset: [] as unknown as [number, number]
      },
      importRecordRef: null
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getWaterMark(): Object {
      return this.waterMark
    },
    getAccountRrror(): Object {
      return this.accountRrror
    },
    getImportRecordRef(): HTMLElement | null {
      return this.importRecordRef
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    async logoutConfirm() {
      const { t } = useI18n()
      const appStore = useAppStore()
      await appStore.waitBeforeLogout()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          uspLogoutRequest().then((res) => {
            if (res) {
              this.logout()
            }
          })
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setRoleRouters([])
      this.setWaterMark({
        content: '',
        invisibleContent: '未登陆',
        invisibleFontSize: 14,
        font: {
          fontSize: 14,
          color: `rgba(0, 0, 0, 0.2)`
        },
        zIndex: 100,
        rotate: -22,
        gap: [100, 100] as [number, number],
        offset: [] as unknown as [number, number],
        isClose: false
      })
    },
    logout() {
      this.reset()
      const permissionStore = usePermissionStoreWithOut()
      permissionStore.setIsAddRouters(false)
      router.replace('/login')
      // 重置根目录
      router.addRoute(resetRoot())
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    setWaterMark(waterMark: Object) {
      this.waterMark = waterMark
    },
    setAccountRrror(accountRrror: Object) {
      this.accountRrror = accountRrror
    },
    //isRestWaterMark 不显示水印
    updateWaterMark(isRestWaterMark?: boolean) {
      const { t } = useI18n()
      // console.log(this.userInfo?.telNumber)
      // this.setWaterMark({
      //   content: this.userInfo?.username + ' ' + this.userInfo?.telNumber?.slice(-4) || '',
      //   // content: '样例数据，演示专用',
      //   invisibleContent: this.userInfo?.username,
      //   // invisibleContent: '样例数据，演示专用',
      //   invisibleFontSize: 16,
      //   font: {
      //     fontWeight: 100,
      //     fontSize: 14,
      //     color: `rgba(0, 0, 0, 0.08)`
      //   },
      //   zIndex: 100,
      //   rotate: -22,
      //   visibleWmDensityLevel: 1,
      //   invisibleWmDensityLevel: 2,
      //   gap: [100, 50] as [number, number],
      //   offset: [] as unknown as [number, number],
      //   isClose: false
      // })
      if (isRestWaterMark) {
        this.setWaterMark({
          content: '',
          invisibleContent: '',
          invisibleFontSize: 14,
          font: {
            fontSize: 14,
            color: `rgba(0, 0, 0, 0.2)`
          },
          zIndex: 100,
          rotate: -22,
          gap: [100, 100] as [number, number],
          offset: [] as unknown as [number, number],
          isClose: false
        })
        return
      }
      getWaterMark({})
        .then((res) => {
          const config = transformWaterMarkConfig(res.data)
          this.setWaterMark(config)
        })
        .catch((e) => {
          if (e.data.code !== 401) {
            // 401 是token过期，不需要提示
            ElMessageBox.alert('', t('common.waterMarkLoadError'), {
              type: 'warning',
              showClose: false,
              center: true,
              confirmButtonText: t('common.refresh')
            }).then(() => {
              window.location.reload()
            })
          }
        })
    },
    setImportRecordRef(virtualRef: HTMLElement | null) {
      this.importRecordRef = virtualRef
    },
    async showNotification(taskName: string, callback = () => {}) {
      const { data } = await getImportResult(taskName)
      const notice = ElNotification({
        title: '数据导入 ' + dayjs().format('YYYY-MM-DD HH:mm:ss'),
        duration: 0,
        offset: 45,
        dangerouslyUseHTMLString: true,
        message: h(ImportProgress, {
          style: 'margin-top: 8px',
          data: data,
          onFinish: () => {
            notice.close()
            callback()
          }
        })
      })
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
